/**
 * LibraryHubPage — the tenant Library hub.
 *
 * Four Radix Tabs: Departments, Positions, Member Profiles, Job Profiles.
 * The active tab is derived from the URL so deep links and browser nav work naturally.
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs } from '@/components/Tabs/Tabs';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { t } from '@/lib/i18n';
import { JobTemplateList } from '../components/JobTemplateList';
import { LibraryList } from '../components/LibraryList';
import { StaffProfileList } from '../components/StaffProfileList';
import styles from './LibraryHubPage.module.css';

type TabValue = 'departments' | 'positions' | 'member-profiles' | 'job-profiles';

function tabFromPath(pathname: string): TabValue {
  if (pathname.startsWith('/library/positions')) return 'positions';
  if (pathname.startsWith('/library/member-profiles')) return 'member-profiles';
  if (pathname.startsWith('/library/job-profiles')) return 'job-profiles';
  return 'departments';
}

export function LibraryHubPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const active = tabFromPath(location.pathname);

  function onTabChange(next: string) {
    void navigate(`/library/${next}`);
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('Library')}
        subtitle={t('Manage the reusable building blocks for your team.')}
      />

      <Tabs.Root value={active} onValueChange={onTabChange}>
        <Tabs.List aria-label={t('Library sections')}>
          <Tabs.Trigger value="departments">{t('Departments')}</Tabs.Trigger>
          <Tabs.Trigger value="positions">{t('Positions')}</Tabs.Trigger>
          <Tabs.Trigger value="member-profiles">{t('Member Profiles')}</Tabs.Trigger>
          <Tabs.Trigger value="job-profiles">{t('Job Profiles')}</Tabs.Trigger>
        </Tabs.List>

        {/*
          Render only the active Tabs.Content. Radix's default is to mount all
          panels and toggle visibility — that leaves N-1 empty hidden tabpanel
          divs in the DOM per render. Since this Root is controlled by URL,
          we know `active` up-front and can omit the inactive panels entirely.
        */}
        {active === 'departments' && (
          <Tabs.Content value="departments">
            <LibraryList kind="unit" />
          </Tabs.Content>
        )}
        {active === 'positions' && (
          <Tabs.Content value="positions">
            <LibraryList kind="position" />
          </Tabs.Content>
        )}
        {active === 'member-profiles' && (
          <Tabs.Content value="member-profiles">
            <StaffProfileList />
          </Tabs.Content>
        )}
        {active === 'job-profiles' && (
          <Tabs.Content value="job-profiles">
            <JobTemplateList />
          </Tabs.Content>
        )}
      </Tabs.Root>
    </div>
  );
}
